export enum SocialPlatform {
  LINKEDIN = 'LINKEDIN',
  INSTAGRAM = 'INSTAGRAM',
  FACEBOOK = 'FACEBOOK',
  TWITTER = 'TWITTER',
  TIKTOK = 'TIKTOK',
}

export enum PostStatus {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  PENDING_PUBLISH = 'PENDING_PUBLISH',
  PUBLISHED = 'PUBLISHED',
  FAILED = 'FAILED',
  ARCHIVED = 'ARCHIVED',
  /** V43: post was synced from a platform but no longer comes back from
   *  that platform's API. Soft-deleted, never hard-deleted. */
  DELETED_ON_PLATFORM = 'DELETED_ON_PLATFORM',
  /** V135: approved post that the publisher couldn't push automatically.
   *  Trigger: LinkedIn share-API mode + company-page connection. UI shows
   *  a "copy + open LinkedIn + I published it" card; confirming flips to
   *  PUBLISHED + `manuallyPublishedAt` stamp. */
  MANUAL_PUBLISH = 'MANUAL_PUBLISH',
}

export enum WorkspaceRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  // V131 — between OWNER and MEMBER. Can approve/reject posts and reply to
  // engagement, but cannot manage connections, billing, or workspace metadata.
  APPROVER = 'APPROVER',
  MEMBER = 'MEMBER',
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  systemRole?: string;
  accessStatus?: string;
  plan?: string;
  onboardingCompleted?: boolean;
  /** Wizard pre-selected account type. Beta invite signups land here
   *  as "business" automatically (V151) so the wizard skips step 1. */
  accountType?: string | null;
  /** V151 — beta invite code the user signed up with, if any.
   *  Wizard reads this to detect "this is a beta user" durably
   *  (sessionStorage doesn't survive the verify-email round trip). */
  signupInviteCode?: string | null;
  deletionRequestedAt?: string | null;
  deletionCompletedAt?: string | null;
  /** "google" | "linkedin" | "email" */
  authProvider?: string;
  emailVerified?: boolean;
  /** UI language preference — "nl" | "en". Default "nl". */
  uiLanguage?: string;
  /** Accent color override — 7-char hex (`#e8540a`). Polish step 3. */
  accentColor?: string;
  createdAt?: string;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  role: WorkspaceRole;
  createdAt: string;
  /** True only for the synthetic workspace inserted into the list when a
   *  platform admin opens a customer workspace from /admin/workspaces. */
  isImpersonated?: boolean;
}

export interface DashboardStats {
  totalBrands: number;
  scheduledPosts: number;
  draftPosts: number;
  publishedPosts: number;
}

export interface CreateWorkspaceRequest {
  name: string;
}

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export type PublishMode = 'BRAND_ACCOUNT' | 'WORKSPACE_ACCOUNT' | 'ASSIGNED';

export const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  [SocialPlatform.LINKEDIN]: 'LinkedIn',
  [SocialPlatform.INSTAGRAM]: 'Instagram',
  [SocialPlatform.FACEBOOK]: 'Facebook',
  [SocialPlatform.TWITTER]: 'X / Twitter',
  [SocialPlatform.TIKTOK]: 'TikTok',
};

export const PLATFORM_COLORS: Record<SocialPlatform, { bg: string; text: string; badge: string }> =
  {
    [SocialPlatform.LINKEDIN]: {
      bg: 'bg-platform-linkedin-bg',
      text: 'text-platform-linkedin',
      badge: 'bg-platform-linkedin',
    },
    [SocialPlatform.INSTAGRAM]: {
      bg: 'bg-platform-instagram-bg',
      text: 'text-platform-instagram',
      badge: 'bg-platform-instagram',
    },
    [SocialPlatform.FACEBOOK]: {
      bg: 'bg-platform-facebook-bg',
      text: 'text-platform-facebook',
      badge: 'bg-platform-facebook',
    },
    [SocialPlatform.TWITTER]: {
      bg: 'bg-platform-twitter-bg',
      text: 'text-platform-twitter',
      badge: 'bg-platform-twitter',
    },
    [SocialPlatform.TIKTOK]: {
      bg: 'bg-platform-tiktok-bg',
      text: 'text-platform-tiktok',
      badge: 'bg-platform-tiktok',
    },
  };

export const STATUS_COLORS: Record<PostStatus, string> = {
  [PostStatus.DRAFT]: 'bg-accent-light text-accent-dark',
  [PostStatus.SCHEDULED]: 'bg-warning-light text-warning',
  [PostStatus.PENDING_PUBLISH]: 'bg-accent-light text-accent',
  [PostStatus.PUBLISHED]: 'bg-success-light text-success',
  [PostStatus.FAILED]: 'bg-danger-light text-danger',
  [PostStatus.ARCHIVED]: 'bg-subtle text-text-tertiary',
  // V43: distinct red-tinted badge for soft-deleted synced posts
  [PostStatus.DELETED_ON_PLATFORM]: 'bg-red-50 text-red-700',
  // V135: amber-tinted pill for posts awaiting human action
  [PostStatus.MANUAL_PUBLISH]: 'bg-amber-500/15 text-amber-500',
};

export type ContentType =
  | 'EDUCATIONAL'
  | 'SOCIAL_PROOF'
  | 'THOUGHT_LEADERSHIP'
  | 'PRODUCT_HIGHLIGHT'
  | 'INDUSTRY_REACTION'
  | 'BEHIND_THE_SCENES';

export const CONTENT_TYPES: ContentType[] = [
  'EDUCATIONAL',
  'SOCIAL_PROOF',
  'THOUGHT_LEADERSHIP',
  'PRODUCT_HIGHLIGHT',
  'INDUSTRY_REACTION',
  'BEHIND_THE_SCENES',
];

export const CONTENT_TYPE_LABELS: Record<string, string> = {
  EDUCATIONAL: 'Educational',
  SOCIAL_PROOF: 'Social Proof',
  THOUGHT_LEADERSHIP: 'Thought Leadership',
  PRODUCT_HIGHLIGHT: 'Product Highlight',
  INDUSTRY_REACTION: 'Industry Reaction',
  BEHIND_THE_SCENES: 'Behind the Scenes',
};
